import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { WarehouseCards } from "../components/WarehouseCards";

export default function Locations() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [fWh, setFWh] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ warehouse: "", aisle: "", rack: "", bin: "" });
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get("/locations", { params: fWh !== "all" ? { warehouse: fWh } : {} }).then((r) => setRows(r.data));
  }, [fWh]);

  useEffect(() => {
    load();
    api.get("/warehouses").then((r) => setWarehouses(r.data));
  }, [load]);

  const save = async () => {
    try {
      if (editId) await api.put(`/locations/${editId}`, form);
      else await api.post("/locations", form);
      toast.success(editId ? t("common.saved") : t("common.created"));
      setOpen(false);
      setForm({ warehouse: "", aisle: "", rack: "", bin: "" });
      setEditId(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/locations/${id}`);
      toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="locations-page">
      <PageHeader title={t("loc.title")} subtitle={t("loc.subtitle")}>
        {can("admin", "warehouse") && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm({ warehouse: "", aisle: "", rack: "", bin: "" }); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-none text-xs font-bold" data-testid="location-new-button">
                <Plus size={13} className="mr-1" /> {t("loc.new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader>
                <DialogTitle className="uppercase font-black">{editId ? t("common.edit") : t("loc.new")}</DialogTitle>
                <DialogDescription className="text-xs">{t("loc.subtitle")}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1 col-span-2">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.warehouse")}</Label>
                  <Select value={form.warehouse} onValueChange={(v) => setForm({ ...form, warehouse: v })}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="location-form-warehouse"><SelectValue /></SelectTrigger>
                    <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.code}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {[["aisle", t("loc.aisle")], ["rack", t("loc.rack")], ["bin", t("loc.binOptional")]].map(([k, l]) => (
                  <div key={k} className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{l}</Label>
                    <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="rounded-none h-8 text-xs" data-testid={`location-form-${k}`} />
                  </div>
                ))}
              </div>
              <Button onClick={save} disabled={!form.warehouse || !form.aisle || !form.rack}
                className="rounded-none font-bold" data-testid="location-save-button">{editId ? t("common.save") : t("common.create")}</Button>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <WarehouseCards warehouses={warehouses} canEdit={can("admin")} onSaved={() => api.get("/warehouses").then((r) => setWarehouses(r.data))} />

      <div className="mb-4">
        <Select value={fWh} onValueChange={setFWh}>
          <SelectTrigger className="rounded-none h-8 text-xs w-56" data-testid="locations-filter-warehouse"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            {warehouses.map((w) => <SelectItem key={w.id} value={w.code}>{w.code}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="locations-table">
          <table className="tbl">
            <thead><tr>
              <th>{t("loc.code")}</th><th>{t("common.warehouse")}</th>
              <th>{t("loc.aisle")}</th><th>{t("loc.rack")}</th><th>{t("loc.bin")}</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="font-bold">{r.code}</td>
                  <td>{r.warehouse}</td>
                  <td>{r.aisle}</td><td>{r.rack}</td><td>{r.bin}</td>
                  <td>
                    {can("admin", "warehouse") && (
                      <div className="flex gap-2">
                        <button data-testid={`location-edit-${r.code}`} className="text-muted-foreground hover:text-primary"
                          onClick={() => { setForm({ warehouse: r.warehouse, aisle: r.aisle, rack: r.rack, bin: r.bin }); setEditId(r.id); setOpen(true); }}>
                          <Pencil size={13} />
                        </button>
                        <button data-testid={`location-delete-${r.code}`} className="text-muted-foreground hover:text-destructive"
                          onClick={() => remove(r.id)}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
